
import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  Autocomplete,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ApprovePartyList,
  ApproveSolistapi,
  GenerateSalesVoucherNo,
  SalesVoucherCreate,
  GetSalesVoucherList,
  SalesVoucherUpdate,
  DivisionList,
} from "../../../Services/Services";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Visibility,
} from "@mui/icons-material";
const SalesVoucherForm = () => {
  const navigate = useNavigate();
  const location = useLocation();


  // 🔹 Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // ✅ Check if edit mode
  const isEditMode = location.state?.editMode || false;
  const editData = location.state?.voucherData || null;

  // States

  const [submitButtonText, setSubmitButtonText] = useState("Submit");
  // ===== ALL STATES FROM ORIGINAL =====
  const [ProjectLists, setProjectLists] = useState([]);
  const [projectPage, setProjectPage] = useState(1);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const [PartyLists, setPartyLists] = useState([]);
  const [partyPage, setPartyPage] = useState(1);
  const [selectedParty, setSelectedParty] = useState(null);
  const [partyLoading, setPartyLoading] = useState(false);
  const [partySearch, setPartySearch] = useState("");
  const [DivisionLists, setDivisionLists] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [rowErrors, setRowErrors] = useState([]);
  const [rows, setRows] = useState([blankRow()]);
  const [loading, setLoading] = useState(false);
  const userId = Number(localStorage.getItem("user_id"));
  const [existingFileName, setExistingFileName] = useState("");

  // ===== INITIAL DATA FETCH =====
  useEffect(() => {
    fetchAllData();
    if (!isEditMode) {
      fetchNextSvNo();
    }
  }, []);

  const fetchAllData = async () => {
    try {
      const [divisions] = await Promise.all([
        DivisionList(),
      ]);
      const divisionsArray = Array.isArray(divisions)
        ? divisions
        : divisions?.results || [];
      setDivisionLists(
        divisionsArray.map((div) => ({
          id: div.division_id,
          name: div.division,
        }))
      );
    } catch (error) {
      toast.error("Error fetching initial data");
    }
  };

  const fetchNextSvNo = async () => {
    const data = await GenerateSalesVoucherNo();
    if (data && data.data.sales_voucher_no) {
      setRows((prev) => {
        const updated = [...prev];
        if (updated.length > 0) {
          updated[0] = {
            ...updated[0],
            sales_number: data.data.sales_voucher_no,
          };
        }
        return updated;
      });
    }
  };
  const fetchProjects = async (page = 1, filters = {}) => {
    try {
      setProjectLoading(true);
      const response = await ApproveSolistapi(page, filters);

      // Response could have .results and .next (like DRF paginated)
      const results = Array.isArray(response.results)
        ? response.results
        : response || [];

      // Merge pages
      if (page === 1) {
        setProjectLists(results);
      } else {
        setProjectLists((prev) => [...prev, ...results]);
      }

      // Pagination control (Django REST style)
      setProjectPage(page);
    } catch (err) {
      toast.error("Failed to load project list");
    } finally {
      setProjectLoading(false);
    }
  };

  
  const getParties = async (pageNum = 1, filters = {}) => {
    try {
      setPartyLoading(true);

      const response = await ApprovePartyList(pageNum, filters);
      const results = Array.isArray(response) ? response : [];

      if (pageNum === 1) {
        setPartyLists(results);
      } else {
        setPartyLists((prev) => [...prev, ...results]);
      }

      setPartyPage(pageNum);
    } catch (err) {
      toast.error("Failed to load parties");
    } finally {
      setPartyLoading(false);
    }
  };
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      // Search filter trigger
      fetchProjects(1, { project_name: projectSearch });
    }, 500); // Wait 0.5s after typing stops

    return () => clearTimeout(delayDebounce);

  }, [projectSearch]);
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      // Search filter trigger
      getParties(1, { name: partySearch });
    }, 500); // Wait 0.5s after typing stops

    return () => clearTimeout(delayDebounce);

  }, [partySearch]);
  // ===== FORM UTILITIES =====
  function blankRow() {
    return {
      sales_voucher_no: "",
      sales_number: "",
      project: "",
      division: "",
      division_name: "",
      party: "",
      invoice_number: "",
      amount: "",
      receipt_amount: "",
      gst_percentage: "",
      gst_amount: "",
      total_amount: "",
      attachment: "",
      payment_status: "",
      invoice_date: "",
      narration: "",
    };
  }

  const handleRowChange = (index, e) => {
    const { name, value, files } = e.target;
    const updated = [...rows];
    updated[index][name] = name === "attachment" ? files[0] : value;
    setRows(updated);
  };

  const handleAmountOrGstChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...rows];
    const row = { ...updated[index] };

    // assign the changed value
    row[name] = value;

    const amount = parseFloat(row.amount) || 0;
    const gstPercent = parseFloat(row.gst_percentage) || 0;
    const gstAmt = parseFloat(row.gst_amount) || 0;

    if (name === "amount" && gstPercent > 0) {
      const newGstAmt = (amount * gstPercent) / 100;
      row.gst_amount = newGstAmt.toFixed(2);
      row.total_amount = (amount + newGstAmt).toFixed(2);
    }
    if (name === "gst_percentage") {
      const newGstAmt = (amount * gstPercent) / 100;
      row.gst_amount = newGstAmt.toFixed(2);
      row.total_amount = (amount + newGstAmt).toFixed(2);
    }
    if (name === "gst_amount") {
      row.total_amount = (amount + gstAmt).toFixed(2);
    }

    updated[index] = row;
    setRows(updated);
  };

  const addRow = () => setRows([...rows, blankRow()]);
  const removeRow = (index) => setRows(rows.filter((_, i) => i !== index));

  const validateForm = (data) => {
    const errors = {};
    const rowErrs = [];

    if (!data.party) errors.party = "Required";
    if (!data.amount) errors.amount = "Required";
    if (!data.payment_status) errors.payment_status = "Required";

    setFormErrors(errors);
    setRowErrors(rowErrs);

    return (
      Object.keys(errors).length === 0 &&
      rowErrs.every((e) => Object.keys(e).length === 0)
    );
  };

  useEffect(() => {
    if (isEditMode && editData) {

      if (editData.attachment) {
        const fullPath = editData.attachment.split('/').pop();
        const fileName = fullPath.split('?')[0];

        if (fileName.length > 20) {
          const ext = fileName.split('.').pop();
          const name = fileName.substring(0, 15);
          setExistingFileName(`${name}...${ext}`);
        } else {
          setExistingFileName(fileName);
        }
      }
      // Set form data from edit
      setRows([
        {
          sales_voucher_no: editData.sales_voucher_no || "",
          sales_number: editData.sales_number || "",
          project: editData.project || "",
          division: editData.division || "",
          division_name: editData.division_name || "",
          party: editData.party || editData.party_id || "", // ✅ Add party ID
          party_name: editData.company_name || "", // ✅ Party name
          invoice_number: editData.invoice_number || "",
          amount: editData.amount || "",
          gst_percentage: editData.gst_percentage || "",
          gst_amount: editData.gst_amount || "",
          total_amount: editData.total_amount || "",
          receipt_amount: editData.receipt_amount || "",
          attachment: editData.attachment || "",
          payment_status: editData.payment_status || "",
          invoice_date: editData.invoice_date || "",
          narration: editData.narration || "",
        },
      ]);

      // Change button text
      setSubmitButtonText("Update");
    } else {
      // New voucher mode
      fetchNextSvNo();
      setSubmitButtonText("Submit");
    }
  }, [isEditMode, editData]);
  // ===== SUBMIT HANDLER =====
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      const row = rows[0]; // Single row for edit

      // Append all fields
      formData.append("sales_voucher_no", row.sales_voucher_no || "");
      formData.append("sales_number", row.sales_number || "");
      formData.append("project", row.project || "");
      formData.append("division", row.division || "");
      formData.append("invoice_date", row.invoice_date || "");
      formData.append("invoice_number", row.invoice_number || "");
      formData.append("party", row.party || "");
      formData.append("amount", row.amount || "");
      formData.append("gst_percentage", row.gst_percentage || "");
      formData.append("gst_amount", row.gst_amount || "");
      formData.append("total_amount", row.total_amount || "");
      formData.append("receipt_amount", row.receipt_amount || "");
      formData.append("payment_status", row.payment_status || "");
      formData.append("narration", row.narration || "");

      if (row.attachment instanceof File) {
        formData.append("attachment", row.attachment);
      }

      let response;
      if (isEditMode) {
        // ✅ Update API call
        formData.append("updated_by", userId);
        response = await SalesVoucherUpdate(row.sales_voucher_no, formData);
        toast.success("Voucher updated successfully!");
      } else {
        // ✅ Create API call
        formData.append("created_by", userId);
        response = await SalesVoucherCreate(formData);
        toast.success("Voucher created successfully!");
      }

      // Navigate back to table
      setTimeout(() => {
        navigate("/dashboard/salesvoucher");
      }, 1500);
    } catch (error) {
      toast.error(isEditMode ? "Update failed!" : "Creation failed!");
    } finally {
      setLoading(false);
    }
  };

  // ===== RENDER =====
  return (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      {/* 🔹 Buttons Section - Mobile Responsive */}
      <Grid
        sx={{
          display: "flex",
          justifyContent: isMobile ? "flex-start" : "flex-end",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 2 : 3,
          alignItems: isMobile ? "stretch" : "flex-end",
          width: "100%",
          mt: isMobile ? -2 : -5,
          pt: 0,
          mb: 2,
          px: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          fullWidth={isMobile}
          sx={{
            borderRadius: 0,
            height: 35,
            minWidth: isMobile ? "100%" : 120,
            fontSize: "0.7rem",
            px: 2,
            textTransform: "none",
          }}
        >
          ← Back
        </Button>
        <Button
          variant="contained"
          type="submit"
          onClick={handleSubmit}
          disabled={loading}
          fullWidth={isMobile}
          sx={{
            borderRadius: 0,
            height: 35,
            minWidth: isMobile ? "100%" : 120,
            fontSize: "0.7rem",
            px: 2,
            textTransform: "none",
          }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : "Submit"}
        </Button>
      </Grid>

      <ToastContainer />

      {/* 🔹 Form Container - Adjusted padding for mobile */}
      <Box sx={{ overflowY: "auto", maxHeight: "calc(100vh - 200px)" }}>
        {rows.map((row, index) => (
          <Box
            key={index}
            sx={{
              mb: 2,
              backgroundColor: "#f5f7fa",
              borderRadius: isMobile ? 1 : 2,
              border: "1px solid #e0e0e0",
              p: isMobile ? 1.5 : 2,
            }}
          >
            <Grid container spacing={isMobile ? 1.5 : 2}>
              {/* 🔹 All form fields - Mobile responsive grid */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Voucher No."
                  name="sales_number"
                  value={row.sales_number}
                  size="small"
                  disabled
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Invoice No."
                  name="invoice_number"
                  value={row.invoice_number || ""}
                  onChange={(e) => handleRowChange(index, e)}
                  size="small"
                  error={!!formErrors.invoice_number}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Invoice Date"
                  name="invoice_date"
                  type="date"
                  value={row.invoice_date || ""}
                  onChange={(e) => handleRowChange(index, e)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!formErrors.invoice_date}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={ProjectLists}
                  getOptionLabel={(option) =>
                    option && option.id ? `${option.project_name}` : ""
                  }
                  value={ProjectLists.find((p) => p.id === row.project) || null}
                  onInputChange={(e, newInputValue) => {
                    setProjectSearch(newInputValue);
                  }}
                  onChange={(e, newValue) => {
                    handleRowChange(index, {
                      target: {
                        name: "project",
                        value: newValue ? newValue.id : "",
                      },
                    });

                    if (newValue) {
                      handleRowChange(index, {
                        target: {
                          name: "division",
                          value: newValue.division || "",
                        },
                      });

                      handleRowChange(index, {
                        target: {
                          name: "division_name",
                          value: newValue.division_name || "",
                        },
                      });
                      handleRowChange(index, {
                        target: {
                          name: "party_name",
                          value: newValue.party_details.company_name || "",
                        },
                      });
                      handleRowChange(index, {
                        target: {
                          name: "party",
                          value: newValue.party_details.id || "",
                        },
                      });

                      const totalAmount =
                        newValue.items?.reduce(
                          (sum, i) => sum + Number(i.amount || 0),
                          0
                        ) || 0;

                      handleRowChange(index, {
                        target: {
                          name: "amount",
                          value: totalAmount.toFixed(2),
                        },
                      });
                    } else {
                      // Reset if cleared
                      ["division", "division_name", "amount", "party"].forEach(
                        (field) =>
                          handleRowChange(index, {
                            target: { name: field, value: "" },
                          })
                      );
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Project"
                      size="small"
                      error={!!formErrors.project}
                      helperText={formErrors.project}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  fullWidth
                  options={DivisionLists}
                  getOptionLabel={(option) => option.name || ""}
                  value={
                    DivisionLists.find((d) => d.id === row.division) || null
                  }
                  onChange={(e, newValue) => {
                    handleRowChange(index, {
                      target: {
                        name: "division",
                        value: newValue ? newValue.id : "",
                      },
                    });
                    handleRowChange(index, {
                      target: {
                        name: "division_name",
                        value: newValue ? newValue.name : "",
                      },
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Division"
                      size="small"
                      error={!!formErrors.division}
                      helperText={formErrors.division}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  fullWidth
                  options={PartyLists}
                  getOptionLabel={(option) => option.company_name || ""}
                  value={PartyLists.find((p) => p.id === row.party) || null}
                  onChange={(e, newValue) => {
                    handleRowChange(index, {
                      target: {
                        name: "party",
                        value: newValue ? newValue.id : "",
                      },
                    });
                    handleRowChange(index, {
                      target: {
                        name: "party_name",
                        value: newValue ? newValue.company_name : "",
                      },
                    });
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    // Only trigger search when user is typing
                    if (reason === "input") setPartySearch(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Debtor"
                      size="small"
                      error={!!formErrors.party}
                      helperText={formErrors.party}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Basic Value"
                  name="amount"
                  value={row.amount || ""}
                  onChange={(e) => handleRowChange(index, e)}
                  size="small"
                  error={!!formErrors.amount}
                // disabled
                />
              </Grid>

              {/* GST fields - Mobile pe 6 columns for side by side */}
              <Grid item xs={6} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="GST %"
                  name="gst_percentage"
                  type="number"
                  value={row.gst_percentage || ""}
                  onChange={(e) => handleAmountOrGstChange(index, e)}
                  size="small"
                  inputProps={{ min: 0, step: "0.01" }}
                />
              </Grid>

              <Grid item xs={6} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="GST Amount"
                  name="gst_amount"
                  type="number"
                  value={row.gst_amount || ""}
                  onChange={(e) => handleAmountOrGstChange(index, e)}
                  size="small"
                  inputProps={{ shrink: true, min: 0, step: "0.01" }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Total Amount"
                  name="total_amount"
                  type="number"
                  value={row.total_amount || ""}
                  onChange={(e) => handleAmountOrGstChange(index, e)}
                  size="small"
                  inputProps={{ min: 0, step: "0.01" }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Payment Status"
                  name="payment_status"
                  value={row.payment_status || ""}
                  onChange={(e) => handleRowChange(index, e)}
                  select
                  size="small"
                  error={!!formErrors.payment_status}
                >
                  <MenuItem value="Paid">Paid</MenuItem>
                  <MenuItem value="Unpaid">Unpaid</MenuItem>
                  <MenuItem value="Partial">Partial</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <TextField
                    fullWidth
                    type="file"
                    name="attachment"
                    id={`attachment-${index}`}
                    onChange={(e) => {
                      handleRowChange(index, e);
                      setExistingFileName(""); // ✅ Clear filename when new file selected
                    }}
                    label={existingFileName ? "Change Attachment" : "Attachment"}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    error={!!formErrors.attachment}
                  />

                  {/* ✅ Show existing file info */}
                  {existingFileName && row.attachment && isEditMode && (
                    <Box
                      mt={0.5}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem', color: 'text.secondary' }}
                    >
                      <span>{isMobile ? existingFileName.substring(0, 15) + '...' : `Current: ${existingFileName}`}</span>
                      {typeof row.attachment === 'string' && (
                        <IconButton
                          size="small"
                          component="a"
                          href={row.attachment}
                          target="_blank"

                        >
                          <Visibility sx={{ fontSize: "1rem", color: "#1976d2" }} />
                        </IconButton>
                      )}
                    </Box>
                  )}

                  {formErrors.attachment && (
                    <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                      {formErrors.attachment}
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Narration - Full width on mobile, 9 columns on desktop */}
              <Grid item xs={12} sm={12} md={9}>
                <TextField
                  fullWidth
                  label="Narration"
                  name="narration"
                  value={row.narration || ""}
                  onChange={(e) => handleRowChange(index, e)}
                  size="small"
                  multiline={isMobile}
                  rows={isMobile ? 2 : 1}
                />
              </Grid>

              {/* Remove Button - Adjust for mobile */}
              {!isMobile && (
                <Grid item xs={12} sm={6} md={3}>
                  {rows.length > 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => removeRow(index)}
                        size="small"
                        startIcon={<Close fontSize="small" />}
                        sx={{ textTransform: "none", fontWeight: 500 }}
                      >
                        Remove
                      </Button>
                    </Box>
                  )}
                </Grid>
              )}

              {/* Mobile Remove Button - Full width */}
              {isMobile && rows.length > 1 && (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    onClick={() => removeRow(index)}
                    size="small"
                    startIcon={<Close fontSize="small" />}
                    sx={{ textTransform: "none", fontWeight: 500, mt: 1 }}
                  >
                    Remove
                  </Button>
                </Grid>
              )}
            </Grid>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default SalesVoucherForm;